// assets/AllCourses/Exercises/SentenceJumblesExercise.jsx

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { StringField } from "@/components/shared/FormFields";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import { cn } from "@/utils/utils";

export default function SentenceJumblesExercise({
  exercise = {},
  onUpdate,            // Edit mode only
  errors = {},
  isPreview = false,   // false = admin edit, true = student view
  showResults = false,
  onComplete = () => {},
  setAttempt = () => {}, // ✅ added for consistency
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= SAFE QUESTIONS ================= */
  const questions = Array.isArray(exercise.sentenceJumblesQuestions)
    ? exercise.sentenceJumblesQuestions.map((q) => ({
        correctSentence:
          typeof q.correctSentence === "string"
            ? q.correctSentence.trim()
            : "",
        jumbledWords: Array.isArray(q.jumbledWords)
          ? q.jumbledWords.map((w) => String(w).trim())
          : [],
        requireCapitalization: Boolean(q.requireCapitalization),
      }))
    : [];

  /* ================= STUDENT STATE ================= */
  const [state, setState] = useState({
    index: 0,
    answers: {}, // { [qIndex]: number[] }
    hasInteracted: false,
  });

  const completedRef = useRef(false);
  const current = questions[state.index];

  /* ================= SHUFFLED WORDS ================= */
  const [shuffled, setShuffled] = useState([]);

  useEffect(() => {
    if (!current) {
      setShuffled([]);
      return;
    }

    setShuffled(
      current.jumbledWords
        .map((word, idx) => ({ word, id: idx }))
        .sort(() => Math.random() - 0.5)
    );
  }, [state.index, questions]);

  /* ================= RESTORE ATTEMPT ================= */
  useEffect(() => {
    if (!isPreview || !exercise.attempt) return;

    setState({
      index: exercise.attempt.index ?? 0,
      answers: exercise.attempt.answers ?? {},
      hasInteracted: !!exercise.attempt.hasInteracted,
    });
  }, [isPreview, exercise.attempt]);

  
  useEffect(() => {
  if (!showResults) completedRef.current = false;
}, [showResults]);

  /* ================= RESET ================= */
  useEffect(() => {
    if (isPreview) completedRef.current = false;
  }, [exercise?._id, isPreview]);

  /* ================= NORMALIZE ================= */
  const normalize = (text = "", requireCap = false) => {
    let t = String(text).trim();
    if (!requireCap) t = t.toLowerCase();
    return t.replace(/[.,!?]/g, "").replace(/\s+/g, " ");
  };

  const userOrder = state.answers[state.index] || [];

  /* ================= EDIT MODE ================= */
  const updateQuestions = (next) => {
    if (isEditMode) onUpdate("sentenceJumblesQuestions", next);
  };

  const handleQuestionChange = (qIdx, field, value) => {
    if (!isEditMode) return;
    const next = [...questions];
    next[qIdx] = { ...next[qIdx], [field]: value };
    updateQuestions(next);
  };

  const handleWordChange = (qIdx, wIdx, value) => {
    if (!isEditMode) return;
    const next = [...questions];
    const words = [...next[qIdx].jumbledWords];
    words[wIdx] = value;
    next[qIdx] = { ...next[qIdx], jumbledWords: words };
    updateQuestions(next);
  };

  const handleAddWord = (qIdx) => {
    if (!isEditMode) return;
    const next = [...questions];
    next[qIdx] = {
      ...next[qIdx],
      jumbledWords: [...next[qIdx].jumbledWords, ""],
    };
    updateQuestions(next);
  };

  const handleRemoveWord = (qIdx, wIdx) => {
    if (!isEditMode) return;
    const next = [...questions];
    next[qIdx] = {
      ...next[qIdx],
      jumbledWords: next[qIdx].jumbledWords.filter((_, i) => i !== wIdx),
    };
    updateQuestions(next);
  };

  const handleCapitalizationChange = (qIdx, checked) => {
    if (!isEditMode) return;
    const next = [...questions];
    next[qIdx] = { ...next[qIdx], requireCapitalization: !!checked };
    updateQuestions(next);
  };

  const handleAddQuestion = () => {
    if (!isEditMode) return;
    updateQuestions([
      ...questions,
      { correctSentence: "", jumbledWords: [], requireCapitalization: false },
    ]);
  };

  const handleRemoveQuestion = (qIdx) => {
    if (!isEditMode) return;
    updateQuestions(questions.filter((_, i) => i !== qIdx));
  };

  /* ================= STUDENT MODE ================= */
  const handleDragStart = (e, id) => {
    if (!isPreview) return;
    e.dataTransfer.setData("text/plain", String(id));
  };

  const handleDrop = (e) => {
    if (!isPreview || showResults) return;
    e.preventDefault();

    const id = Number(e.dataTransfer.getData("text/plain"));
    if (Number.isNaN(id) || userOrder.includes(id)) return;

    setState((prev) => {
      const nextOrder = [...userOrder, id];

      const nextState = {
        ...prev,
        answers: {
          ...prev.answers,
          [state.index]: nextOrder,
        },
        hasInteracted: true,
      };

      setAttempt(nextState);

      const built = nextOrder
        .map((i) => current.jumbledWords[i])
        .join(" ");

      const complete =
        nextOrder.length === current.jumbledWords.length;

      if (complete) {
        const correct =
          normalize(built, current.requireCapitalization) ===
          normalize(
            current.correctSentence,
            current.requireCapitalization
          );

        const allAnswered =
          Object.keys(nextState.answers).length === questions.length;

        if (allAnswered && !completedRef.current) {
          completedRef.current = true;
          onComplete(exercise._id, correct);
        }
      }

      return nextState;
    });
  };

  const goNext = () => {
    if (state.index >= questions.length - 1) return;
    setState((p) => ({ ...p, index: p.index + 1 }));
  };

  /* ================= EDIT MODE RENDER ================= */
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {questions.map((q, qIdx) => (
          <Card key={qIdx} className="p-6 space-y-6">
            <StringField
              label={`Correct Sentence ${qIdx + 1}`}
              value={q.correctSentence}
              onChange={(v) =>
                handleQuestionChange(qIdx, "correctSentence", v)
              }
              required
            />

            <div className="space-y-3">
              <label className="font-medium">Jumbled Words</label>
              {q.jumbledWords.map((word, wIdx) => (
                <div key={wIdx} className="flex gap-3 items-end">
                  <StringField
                    value={word}
                    onChange={(v) =>
                      handleWordChange(qIdx, wIdx, v)
                    }
                  />
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleRemoveWord(qIdx, wIdx)}
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => handleAddWord(qIdx)}>
                Add Word
              </Button>
            </div>

            <CapitalizationRule
              checked={q.requireCapitalization}
              onChange={(c) => handleCapitalizationChange(qIdx, c)}
            />
          </Card>
        ))}

        <Button variant="outline" onClick={handleAddQuestion}>
          Add Question
        </Button>
      </div>
    );
  }

  /* ================= STUDENT RENDER ================= */
  if (!current) {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardDescription>No sentence jumbles available.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const builtSentence = userOrder
    .map((i) => current.jumbledWords[i])
    .join(" ");

  const isComplete =
    userOrder.length === current.jumbledWords.length;

  const isCorrect =
    isComplete &&
    normalize(builtSentence, current.requireCapitalization) ===
      normalize(
        current.correctSentence,
        current.requireCapitalization
      );

  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle>Sentence Jumbles</CardTitle>
        <CardDescription>
          Rearrange the words • Question {state.index + 1} of{" "}
          {questions.length}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-10">
        {/* Word bank */}
        <div className="flex flex-wrap justify-center gap-4 p-6 bg-muted rounded-xl">
          {shuffled.map(({ word, id }) => (
            <Button
              key={id}
              draggable
              onDragStart={(e) => handleDragStart(e, id)}
              disabled={userOrder.includes(id) || showResults}
              variant="secondary"
              className={cn(
                "cursor-grab text-lg px-6 py-4 transition",

                userOrder.includes(id) && showResults && isCorrect &&
                  "bg-green-100 border border-green-300",

                userOrder.includes(id) && showResults && !isCorrect &&
                  "bg-red-100 border border-red-300",

                userOrder.includes(id) && !showResults && "opacity-50"
              )}
            >
              {word}
            </Button>
          ))}
        </div>

        {/* Drop zone */}
        <div
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
         className={cn(
            "min-h-32 border-2 border-dashed rounded-2xl p-8 text-center transition",

            // correct
            showResults && isComplete && isCorrect &&
              "bg-green-100 border-green-300",

            // wrong
            showResults && isComplete && !isCorrect &&
              "bg-red-100 border-red-300"
          )}
        >
          {userOrder.length === 0 ? (
            <p className="text-gray-500 text-lg">
              Drag words here to form the sentence
            </p>
          ) : (
            <p className="text-2xl font-bold">{builtSentence}</p>
          )}
        </div>

        {/* Feedback */}
        {showResults && isComplete && (
          <div className="text-center space-y-2">
            <p
              className={cn(
                "text-3xl font-bold",
                isCorrect ? "text-green-700" : "text-red-700"
              )}
            >
              {isCorrect ? "Correct!" : "Incorrect"}
            </p>
            {!isCorrect && (
              <p className="text-xl">
                Correct sentence:{" "}
                <strong>{current.correctSentence}</strong>
              </p>
            )}
          </div>
        )}

        {/* Next */}
        {isComplete && state.index < questions.length - 1 && !showResults && (
          <div className="text-center">
            <Button size="lg" onClick={goNext}>
              Next →
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
